import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from '../store';
import { DefaultRes } from '../types';

const env = process.env.REACT_APP_ENV || 'dev';

// ENVS contains the URI string, and for now handles version i.e. v1
export const ENVS: Record<string, { BASE_URL: string }> = {
    local: {
        BASE_URL: 'http://localhost:3333/v1',
    },
    dev: {
        BASE_URL: 'https://aware-api-dev.pom-bright.com/v1',
    },
    stg: {
        BASE_URL: 'https://aware-api-qa.pom-bright.com/v1',
    },
    prd: {
        BASE_URL: 'https://aware-api.pom-bright.com/v1',
    },
    ngrok: {
        // FOR NGROK USE: Update this to reflect the specific ngrok url on start
        BASE_URL: 'https://6bb3e7ad20ce.ngrok-free.app/v1',
    }
};
export const api = axios.create({
    baseURL: ENVS[env].BASE_URL,
});

declare module 'axios' {
    interface AxiosRequestConfig {
        /** The caller shows its own message for a 403; don't fire permissionsChanged. */
        skipPermissionsChanged?: boolean;
    }
}

export const isRequestCanceled = (error: any) =>
    error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

export const getTranscriptRecordFile = async (transcriptId: string): Promise<string> => {
    try {
        console.log('Fetching transcript record file for ID:', transcriptId);
        const response = await api.get(`/integrations/s3/viewRecordFile?transcriptId=${transcriptId}`, {
            responseType: 'blob'
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        console.log('Response data type:', typeof response.data);
        console.log('Response data size:', response.data?.size || 'unknown');

        // Check if the response is actually a blob
        if (response.data instanceof Blob) {
            console.log('Response is a blob, creating object URL');
            const blob = new Blob([response.data], { type: 'audio/wav' });
            const url = URL.createObjectURL(blob);
            console.log('Created blob URL:', url);
            return url;
        } else {
            console.error('Response is not a blob:', response.data);
            throw new Error('Response is not a blob');
        }
    } catch (error) {
        console.error('Error fetching transcript record file:', error);
        throw error;
    }
}

const onRequest = (axiosConfig: AxiosRequestConfig): AxiosRequestConfig => {
    const headers: Record<string, string> = {
        'authorization': `${store.getState().auth.token}`,
    };

    // Add ngrok "ngrok-skip-browser-warning": "true" when using the ngrok env
    if (env === 'ngrok') {
        headers['ngrok-skip-browser-warning'] = 'true';
    }

    return {
        ...axiosConfig,
        headers,
    };
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => Promise.reject(error);

// 401 codes from pom-2-api that mean the session itself is gone
// (src/middlewares/verifyJwt.ts and the v2 settings controller). Every other
// 401 the API sends (MUST_BE_POM_ADMIN, NOT_AUTHORIZED, CUSTOMER_NOT_FOUND,
// YOU CANNOT DELETE YOURSELF, ...) is an authorization failure on a live
// session and must not end it.
export const SESSION_EXPIRED_CODES = ['MISSING_TOKEN', 'TOKEN_NOT_VALID', 'TOKEN_EXPIRED'];

/**
 * True when a failed request means the current session is dead and the user
 * must log in again. Shared by every axios instance so they agree.
 *
 * Ignores stale responses: a request sent with a token that is no longer the
 * one in the store (the user already logged out, or logged in again while it
 * was in flight) says nothing about the current session.
 */
export const isSessionExpiredError = (error: any): boolean => {
    if (error?.response?.status !== 401) {
        return false;
    }

    const message = error.response?.data?.message;
    if (message && !SESSION_EXPIRED_CODES.includes(message)) {
        return false;
    }

    const current = store.getState().auth?.token;
    if (!current) {
        return false;
    }

    const sent = error.config?.headers?.authorization;
    if (sent !== undefined && `${sent}` !== `${current}`) {
        return false;
    }

    return true;
};

const onResponseError = (error: AxiosError<DefaultRes>): Promise<AxiosError> => {
    // A dead session (see isSessionExpiredError): the listener in
    // DashboardLayout logs the user out.
    if (isSessionExpiredError(error)) {
        window.dispatchEvent(new Event('sessionExpired'));
    }

    // Check if 403 error (permissions changed). A call that handles its own
    // 403s opts out, so a feature-level refusal doesn't send the user to the
    // dashboard with "Your permissions have been updated".
    if (error.response?.status === 403 && !error.config?.skipPermissionsChanged) {
        // Dispatch a permissions changed event
        window.dispatchEvent(new Event('permissionsChanged'));
    }

    return Promise.reject(error);
};

const onResponse = (response: AxiosResponse<DefaultRes>) => {
    return response;
};
// @ts-ignore
api.interceptors.request.use(onRequest, onRequestError);
// @ts-ignore
api.interceptors.response.use(onResponse, onResponseError);
